import './src/styles/global.css';
import { setSmoothScrollEnabled } from './src/utils/smoothScroll';
import { installTopLoader, finishTopLoader } from './src/utils/topLoader';
import { warmAuroraWhenIdle } from './src/utils/aurora';
import { recordPreviousPath } from './src/utils/routeHistory';
import { deferHoverPrefetch } from './src/utils/hoverPrefetch';

const usesSmoothScroll = pathname => {
  const path = pathname.replace(/\/+$/, '');
  return path !== '' && path !== '/';
};

function renderKatex() {
  if (typeof window === 'undefined') return;
  if (typeof window.renderMathInElement !== 'function') {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (typeof window.renderMathInElement === 'function') {
        clearInterval(interval);
        runRender();
      } else if (attempts > 40) {
        clearInterval(interval);
      }
    }, 100);
    return;
  }
  runRender();
}

function runRender() {
  const container = document.querySelector('article') || document.body;
  window.renderMathInElement(container, {
    delimiters: [
      { left: '$$', right: '$$', display: true },
      { left: '$', right: '$', display: false },
      { left: '\\[', right: '\\]', display: true },
      { left: '\\(', right: '\\)', display: false },
    ],
    throwOnError: false,
  });
}

const clamp = (value, min, max) => (value < min ? min : value > max ? max : value);

const NEKO_SIZE_RATIO = 1.7;
const NEKO_SIZE_MIN = 26;
const NEKO_SIZE_MAX = 40;
const NEKO_GAP_RATIO = 0.9;
const NEKO_GAP_MIN = 12;
const NEKO_GAP_MAX = 28;

const NEKO_MAX_BOOST = 1.35;
const NEKO_BOOST_NEAR = 96;
const NEKO_BOOST_FAR_RATIO = 0.3;
const NEKO_BOOST_FAR_MIN = 380;
const NEKO_BOOST_FAR_MAX = 640;
const NEKO_BOOST_ACCEL = 0.1;
const NEKO_BOOST_DECEL = 0.18;

const logoInkRect = logo => {
  const glyphs = logo.querySelector("g");

  if (glyphs) {
    const rect = glyphs.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return rect;
  }

  return logo.getBoundingClientRect();
};

const nekoRestingSpot = logo => {
  const ink = logoInkRect(logo);
  const size = Math.round(
    clamp(ink.height * NEKO_SIZE_RATIO, NEKO_SIZE_MIN, NEKO_SIZE_MAX),
  );
  const gap = Math.round(
    clamp(ink.height * NEKO_GAP_RATIO, NEKO_GAP_MIN, NEKO_GAP_MAX),
  );

  return {
    size,
    x: Math.round(ink.right + gap + size / 2),
    y: Math.round(ink.top + ink.height / 2),
  };
};

const installNekoBoost = cat => {
  const baseSpeed = cat.speed;
  const step = cat.frame.bind(cat);
  let boost = 1;

  cat.frame = () => {
    const distance = Math.hypot(cat.targetX - cat.x, cat.targetY - cat.y);

    const far = clamp(
      Math.hypot(window.innerWidth, window.innerHeight) * NEKO_BOOST_FAR_RATIO,
      NEKO_BOOST_FAR_MIN,
      NEKO_BOOST_FAR_MAX,
    );
    const reach = clamp(
      (distance - NEKO_BOOST_NEAR) / (far - NEKO_BOOST_NEAR),
      0,
      1,
    );

    const target = 1 + (NEKO_MAX_BOOST - 1) * reach * reach * (3 - 2 * reach);

    boost +=
      (target - boost) *
      (target > boost ? NEKO_BOOST_ACCEL : NEKO_BOOST_DECEL);
    cat.speed = baseSpeed * boost;

    return step();
  };

  return () => {
    boost = 1;
    cat.speed = baseSpeed;
  };
};

const attachNekoSpeechBubble = cat => {
  const catPhrases = [
    "Hey!", "Hiya!", "Sup?", "Yo!", "Meow!",
    "Hello!", "Welcome!", "Boop!", "Purrfect!", "Catch me!",
    "Play?", "Pets?", "Blep.", "Snacks?", "Gotcha!",
    "Hi there!", "Rawr!", "Nyah!", "Awake!", "Greetings!"
  ];

  cat.element.addEventListener("click", () => {
    if (document.getElementById("cat-speech-bubble")) return;

    const bubble = document.createElement("div");
    bubble.id = "cat-speech-bubble";

    if (cat.idleAnimation === "sleeping") {
      bubble.innerText = "💤";
    } else {
      bubble.innerText = catPhrases[Math.floor(Math.random() * catPhrases.length)];
    }

    Object.assign(bubble.style, {
      position: "absolute",
      top: "-30px",
      left: "50%",
      transform: "translateX(-50%)",
      background: "#ffffff",
      color: "#101626",
      padding: "4px 10px",
      borderRadius: "12px",
      fontSize: "13px",
      fontWeight: "600",
      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
      pointerEvents: "none",
      whiteSpace: "nowrap",
      opacity: "0",
      transition: "opacity 0.2s ease-in-out",
      zIndex: "999999"
    });

    const arrow = document.createElement("div");
    Object.assign(arrow.style, {
      position: "absolute",
      bottom: "-4px",
      left: "50%",
      transform: "translateX(-50%) rotate(45deg)",
      width: "8px",
      height: "8px",
      background: "#ffffff",
      zIndex: "-1"
    });
    bubble.appendChild(arrow);

    cat.element.appendChild(bubble);

    requestAnimationFrame(() => {
      bubble.style.opacity = "1";
    });

    setTimeout(() => {
      bubble.style.opacity = "0";
      setTimeout(() => {
        if (bubble.parentNode) {
          bubble.parentNode.removeChild(bubble);
        }
      }, 200);
    }, 1500);
  });
};

export const onInitialClientRender = () => {
  renderKatex();
  installTopLoader();
  warmAuroraWhenIdle();

  const initNeko = () => {
    import("lots-o-nekos")
      .then(({ Oneko }) => {
        if (typeof Oneko.canInitialize === "function" && !Oneko.canInitialize()) {
          return;
        }

        const logo = document.getElementById("neilblaze-logo");
        const spot = logo ? nekoRestingSpot(logo) : null;

        const cat = new Oneko(
          spot
            ? {
              source: "/oneko.png",
              size: spot.size,
              x: spot.x,
              y: spot.y,
              targetX: spot.x,
              targetY: spot.y,
            }
            : { source: "/oneko.png" },
        );

        if (!cat || !cat.element) return;

        cat.setSprite("idle", 0);

        const resetBoost = installNekoBoost(cat);

        let hasMovedMouse = false;

        window.addEventListener("mousemove", (event) => {
          hasMovedMouse = true;
          cat.setTarget(event.clientX, event.clientY);
        });

        cat.element.id = cat.element.id || "neko-cat";
        if (!document.getElementById("neko-mobile-style")) {
          const style = document.createElement("style");
          style.id = "neko-mobile-style";
          style.innerHTML = `@media (max-width: 768px) { #${cat.element.id} { display: none !important; } }`;
          document.head.appendChild(style);
        }

        cat.element.style.pointerEvents = "auto";
        cat.element.style.cursor = "pointer";

        const isHome = window.location.pathname === '/' || window.location.pathname === '/authors' || window.location.pathname === '/authors/';
        cat.element.style.display = isHome ? 'block' : 'none';

        attachNekoSpeechBubble(cat);

        if (logo) {
          const reseat = () => {
            if (hasMovedMouse) return;

            const next = nekoRestingSpot(logo);

            cat.size = next.size;
            cat.x = next.x;
            cat.y = next.y;
            cat.setTarget(next.x, next.y);
            cat.idleTime = 0;
            cat.resetIdleAnimation();
            resetBoost();
            cat.setSprite("idle", 0);
          };

          requestAnimationFrame(reseat);

          window.addEventListener("resize", reseat);

          if (typeof ResizeObserver !== "undefined") {
            new ResizeObserver(reseat).observe(logo);
          }
        }
      })
      .catch(console.error);
  };

  if (typeof window !== "undefined") {
    const preloader = document.getElementById("nb-preloader");
    if (preloader) {
      window.addEventListener("preloader-complete", initNeko, { once: true });
    } else {
      initNeko();
    }
  } else {
    initNeko();
  }
};

export const onClientEntry = () => {
  deferHoverPrefetch();
};

export const onPreRouteUpdate = ({ prevLocation }) => {
  recordPreviousPath(prevLocation);
};

export const onRouteUpdate = ({ location }) => {
  finishTopLoader();
  setSmoothScrollEnabled(usesSmoothScroll(location.pathname));
  warmAuroraWhenIdle();

  const cat = document.getElementById("neko-cat");
  if (cat) {
    const isHome = location.pathname === '/' || location.pathname === '/authors' || location.pathname === '/authors/';
    cat.style.display = isHome ? 'block' : 'none';
  }
  setTimeout(renderKatex, 0);
};
